import type { MapMouseEvent } from 'mapbox-gl';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useStateRef from '@alltrails/core/hooks/useStateRef';
import useValueRef from '@alltrails/core/hooks/useValueRef';
import { MarkerProperties } from '@alltrails/types/CustomRoute';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { selectIsDrawingEnabled, setIsSmartRouting } from '../../../redux/customRoute';
import { updateCursor } from '../../../redux/reducer';
import { LatLng } from '../../../types/Geo';
import {
  customRouteActiveSegmentChevronsId,
  customRouteActiveSegmentPolylineId,
  customRouteCutSegmentEndMarkerId,
  customRouteMarkersId,
  osmLinesId
} from '../../../utils/layers';
import { MapLayerInteractionsContext } from '../../MapLayerInteractionsProvider';
import useSegmentActions from '../useSegmentActions';
import LinkableEndpointIndicator from './LinkableEndpointIndicator';
import useAddHandle from './useAddHandle';
import useDragMarker from './useDragMarker';
import useFreeDraw from './useFreeDraw';
import useOverrideSmartRouting from './useOverrideSmartRouting';
import useRouteToPoint, { PendingHandle } from './useRouteToPoint';

export const CURSOR_DEFAULT = 'unset';
export const CURSOR_DRAWING = 'crosshair';
export const CURSOR_MARKER = 'pointer';

type DrawControlsContextType = {
  pendingHandle?: PendingHandle;
  dragMarker?: MarkerProperties;
  hoveredMarker?: MarkerProperties;
  freeDrawPoints?: LatLng[];
  isContextMenuOpen: boolean;
  isContextMenuOpenRef: React.MutableRefObject<boolean>;
  setIsContextMenuOpen: (isOpen: boolean) => void;
  showPositionTooltip: boolean;
};

export type DrawControlsOptions = {
  /** Places the handle on the top line where lines of the active segment overlap, or the bottom line while pressing "v" */
  allowSpecificMarkerPlacementWhenLinesOverlap?: boolean;
  /** Moves a segment's start or end placed or dragged near the neighboring segment's endpoint onto it and links the two */
  linkNearbySegmentEndpoints?: boolean;
  /** Shows the position tooltip (back selected/out selected) when hovering over a marker on an overlapping line */
  showPositionTooltip?: boolean;
};

export const DrawControlsContext = createContext<DrawControlsContextType>({
  pendingHandle: undefined,
  dragMarker: undefined,
  hoveredMarker: undefined,
  freeDrawPoints: undefined,
  isContextMenuOpen: false,
  isContextMenuOpenRef: { current: false },
  setIsContextMenuOpen: () => {},
  showPositionTooltip: false
});

/**
 * Keep drawing state separate from the redux store until drawing is complete. This greatly improves performance while drawing since drawing involves rapid state changes.
 * While we separate the state for each drawing feature into hooks we define all event handlers in this provider since MapLayerInteractionsProvider enforces a single event handler per event type per layer.
 * @param children components that need access to the drawing state
 * @param options options for the draw controls
 * @returns a provider that wraps the children and provides the drawing state
 */
const DrawControlsProvider = ({ children, disabled, options }: { children: ReactNode; disabled?: boolean; options?: DrawControlsOptions }) => {
  const dispatch = useAppDispatch();

  const { hasPermission } = useAuthorization();
  // Only DI/admin users (trails:manage) can toggle snap-to-paths with the Control key.
  const canToggleSmartRouting = hasPermission({ permission: 'trails:manage' });

  // Store drawing state in refs to prevent re-registering event handlers as the state changes
  const isInMarkerRef = useRef(false);
  const [isContextMenuOpen, setIsContextMenuOpen, isContextMenuOpenRef] = useStateRef(false);
  const [hoveredMarker, setHoveredMarker] = useState<MarkerProperties | undefined>(undefined);
  const [preferredLinePosition, setPreferredLinePosition] = useState<'out' | 'back' | 'both' | undefined>(
    options?.allowSpecificMarkerPlacementWhenLinesOverlap ? 'back' : undefined
  );
  // Note that the actual delay used for double clicks varies by device and browser so this will never work perfectly
  // Double click detection adds a lot of complexity and makes timing with animations more difficult
  // If double click detection becomes an issue we should instead disable mapbox's double click to zoom or just accept that double clicking will add two markers
  // To do so we can pass doubleClickZoom: false to the controlsConfig prop of BaseMap on the pages using this component
  const doubleClickDelay = 300;
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { isSmartRoutingRef, override: overrideSmartRouting, restore: restoreSmartRouting } = useOverrideSmartRouting();
  const { routeToPoint, pendingHandle } = useRouteToPoint(isSmartRoutingRef, options);

  const addHandle = useAddHandle(preferredLinePosition);

  const {
    isStartingRef: isFreeDrawStartingRef,
    isPendingRef: isFreeDrawPendingRef,
    points: freeDrawPoints,
    start: startFreeDraw,
    update: updateFreeDraw,
    save: saveFreeDraw
  } = useFreeDraw(isSmartRoutingRef);

  const {
    isStartingRef: isDragMarkerStartingRef,
    isPendingRef: isDragMarkerPendingRef,
    pendingMarker: dragMarker,
    start: startDragMarker,
    update: updateDragMarker,
    save: saveDragMarker
  } = useDragMarker(options);

  const { undo, redo, removePoint } = useSegmentActions();

  const { registerEventHandler, unregisterEventHandler, registerBaseMapEventHandlers, unregisterBaseMapEventHandlers } =
    useContext(MapLayerInteractionsContext);

  // This doesn't need to be a ref since we only need event handlers when drawing is enabled
  const isDrawingSelected = useAppSelector(selectIsDrawingEnabled);
  const { isPOISelected, isMapFeatureSelected, cursor, isRouteBuildingEnabled, cutData } = useAppSelector(state => ({
    isPOISelected: state.map.selectedPOI,
    isMapFeatureSelected: state.map.isMapFeatureSelected,
    cursor: state.map.cursor,
    isRouteBuildingEnabled: state.customRoute.isRouteBuildingEnabled,
    cutData: state.customRoute.cutData
  }));
  // Route building must be enabled (build mode) for point placement to be active; in interact mode clicks fall through to overlays.
  const isDrawingEnabled = isDrawingSelected && isRouteBuildingEnabled && !disabled;
  const isDisabledRef = useValueRef(disabled);

  // Check if any drawing action is pending
  const getIsActionPending = useCallback(
    () => isFreeDrawPendingRef.current || isDragMarkerPendingRef.current,
    [isFreeDrawPendingRef, isDragMarkerPendingRef]
  );

  // Check if any drawing action is starting or pending
  const getIsActionStarting = useCallback(
    () => isFreeDrawStartingRef.current || isDragMarkerStartingRef.current || getIsActionPending(),
    [isFreeDrawStartingRef, isDragMarkerStartingRef, getIsActionPending]
  );

  const resetCursor = useCallback(() => {
    if (isInMarkerRef.current) {
      dispatch(updateCursor(CURSOR_MARKER));
    } else {
      dispatch(updateCursor(CURSOR_DEFAULT));
    }
  }, [dispatch]);

  // Update the cursor when drawing is enabled
  useEffect(() => {
    dispatch(updateCursor(isDrawingEnabled ? CURSOR_DRAWING : CURSOR_DEFAULT));
  }, [dispatch, isDrawingEnabled]);

  // Update the cursor if it's set to "" after hovering or clicking a POI
  useEffect(() => {
    if (cursor === '' && isDrawingEnabled) {
      dispatch(updateCursor(CURSOR_DRAWING));
    }
  }, [cursor, dispatch, isDrawingEnabled]);

  // Register keyboard handlers
  useEffect(() => {
    if (!isDrawingEnabled) {
      return;
    }

    // Ignore keyboard events that are triggered by text elements
    const isTextElement = (event: KeyboardEvent) => {
      const eventTarget = event.target as HTMLElement;
      return eventTarget && eventTarget.tagName && ['input', 'textarea'].includes(eventTarget.tagName.toLowerCase());
    };

    // Track whether Control was pressed without any other key — used to toggle smart routing on keyup.
    let controlPressedAlone = false;

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTextElement(event)) {
        return;
      }

      // If another key is pressed while Control is held this is a combination (e.g. Ctrl+z), not a solo Control press.
      if (event.ctrlKey && event.key !== 'Control') {
        controlPressedAlone = false;
      }

      // Likewise another key pressed while Alt is held is a combination (e.g. an admin basemap hotkey), not free draw.
      if (event.altKey && event.key !== 'Alt') {
        restoreSmartRouting();
      }

      // Shown to users by useDrawControlsShortcutLabels: update it along with the undo, redo and free draw bindings
      // Take care to add key combinations from most specific to least specific
      // e.g. Ctrl+Shift+z should be handled before Ctrl+z so that redo does not trigger undo

      if (
        (event.key === 'y' && (event.ctrlKey || event.metaKey)) ||
        // On mac Cmd+Shift comes in with 'z' but Ctrl+Shift comes in with 'Z'
        ((event.key === 'z' || event.key === 'Z') && event.shiftKey && (event.ctrlKey || event.metaKey))
      ) {
        redo('keyboard');
      } else if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
        undo('keyboard');
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        removePoint();
        setIsContextMenuOpen(false);
      } else if (event.key === 'Alt' && !event.ctrlKey && !event.metaKey) {
        overrideSmartRouting();
      } else if (event.key === 'Control' && canToggleSmartRouting && !event.repeat) {
        // Mark Control as potentially solo; actual toggle happens on keyup to avoid firing during Ctrl+z etc.
        controlPressedAlone = true;
        return;
      } else if (event.key === 'v' && options?.allowSpecificMarkerPlacementWhenLinesOverlap) {
        setPreferredLinePosition('out');
      } else if (event.key === 'a' && options?.allowSpecificMarkerPlacementWhenLinesOverlap) {
        setPreferredLinePosition('both');
      } else {
        // Don't prevent default behavior if we are not handling the event
        return;
      }

      // Prevent default key combination behavior if we are handling the event
      event.preventDefault();
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (isTextElement(event)) {
        return;
      }

      if (event.key === 'Control' && canToggleSmartRouting && controlPressedAlone) {
        dispatch(setIsSmartRouting(!isSmartRoutingRef.current));
        controlPressedAlone = false;
      }

      if (event.key === 'Alt') {
        saveFreeDraw();
        restoreSmartRouting();
        resetCursor();
        // A lone Alt press and release focuses the browser menu on Windows
        event.preventDefault();
      }
      if (event.key === 'v' || event.key === 'a') {
        setPreferredLinePosition(options?.allowSpecificMarkerPlacementWhenLinesOverlap ? 'back' : undefined);
      }
    };

    // Alt keyup never arrives if the window loses focus while Alt is held (e.g. Alt+Tab)
    const onBlur = () => {
      restoreSmartRouting();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [
    isDrawingEnabled,
    setIsContextMenuOpen,
    overrideSmartRouting,
    canToggleSmartRouting,
    isSmartRoutingRef,
    undo,
    redo,
    removePoint,
    resetCursor,
    restoreSmartRouting,
    saveFreeDraw,
    dispatch
  ]);

  // Register mouse handlers
  useEffect(() => {
    if (!isDrawingEnabled) {
      return;
    }

    const withoutDoubleClick = (callback: (event: MapMouseEvent) => void) => {
      return (event: MapMouseEvent) => {
        // If a click is already in progress, this is a double click and we can ignore both the previous and current clicks
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
          clickTimeoutRef.current = null;
          return;
        }

        // The features array of a MapMouseEvent is not guaranteed to exist if we wait to access it until after the timeout so we must clone the event
        // Since some properties of the event are non-enumerable we must use Object.assign instead of the spread operator to get a complete clone
        const clonedEvent = Object.assign({}, event);
        if (event.features) {
          clonedEvent.features = event.features.map(feature => Object.assign({}, feature));
        }

        clickTimeoutRef.current = setTimeout(async () => {
          // Await the callback to ensure that the timeout is not cleared until the callback has completed
          // This prevents a race condition between this callback and the callback for any clicks that occur while this one is still pending
          if (!isDisabledRef.current) {
            await callback(clonedEvent);
          }
          clickTimeoutRef.current = null;
        }, doubleClickDelay);
      };
    };

    // Block the default behavior of clicks when the context menu is open
    const whileContextMenuClosed = (callback: (event: MapMouseEvent) => void) => {
      return (event: MapMouseEvent) => {
        if (isContextMenuOpenRef.current) {
          return;
        }

        callback(event);
      };
    };

    const onMapClickBase = (event: MapMouseEvent) => {
      if (getIsActionPending() || isContextMenuOpenRef.current || isPOISelected || isMapFeatureSelected) {
        return;
      }

      return routeToPoint({ coordinates: event.lngLat });
    };
    const onMapClick = whileContextMenuClosed(withoutDoubleClick(onMapClickBase));

    const onActivePolylineClickBase = (event: MapMouseEvent) => {
      if (getIsActionPending() || isContextMenuOpenRef.current) {
        return false;
      }

      return addHandle(event);
    };
    const onActivePolylineClick = whileContextMenuClosed(withoutDoubleClick(onActivePolylineClickBase));

    const onCutSegmentEndMarkerClickBase = () => {
      if (getIsActionPending() || isContextMenuOpenRef.current || !cutData) {
        return false;
      }

      return routeToPoint({ coordinates: { lat: cutData.targetLatLng[0], lng: cutData.targetLatLng[1] }, closeCutSegment: true });
    };
    const onCutSegmentEndMarkerClick = whileContextMenuClosed(withoutDoubleClick(onCutSegmentEndMarkerClickBase));

    const onOsmLineClickBase = (event: MapMouseEvent) => {
      // Attempt to treat the click as a click on the active segment polyline since OSM lines render above the active segment polyline
      const result = onActivePolylineClickBase(event);
      if (result) {
        return;
      }

      // If the click was not on the active segment polyline, treat it as a click on the base map
      onMapClickBase(event);
    };
    const onOsmLineClick = whileContextMenuClosed(withoutDoubleClick(onOsmLineClickBase));

    const onMarkerMouseEnter = (event: MapMouseEvent) => {
      isInMarkerRef.current = true;

      if (!getIsActionPending()) {
        dispatch(updateCursor(CURSOR_MARKER));
      }

      if (event.features) {
        setHoveredMarker(event.features[0].properties as MarkerProperties);
      }
    };

    const onMarkerMouseLeave = () => {
      setHoveredMarker(undefined);
      if (!getIsActionPending()) {
        // This is only safe to skip while in draw mode if we are guaranteed that smart routing will always result in new paths with a marker under the cursor
        // (i.e. we append a straight line from the end of the server's smart routing result to the cursor if the result does not end at the cursor)
        // If this is not true the marker for new paths can render away from the cursor without triggering a mouseleave event
        isInMarkerRef.current = false;
        dispatch(updateCursor(CURSOR_DRAWING));
      }
    };

    const onMouseDown = (event: MapMouseEvent) => {
      // Alt (Option on a Mac) rather than Shift, which Mapbox reserves for box zoom
      if (event.originalEvent.altKey) {
        startFreeDraw(event);
      }
    };

    const onMarkerMouseDown = (event: MapMouseEvent) => {
      onMouseDown(event);

      if (!getIsActionStarting()) {
        startDragMarker(event);
      }
    };

    const onMouseUp = () => {
      if (getIsActionStarting()) {
        resetCursor();
      }

      saveFreeDraw();
      saveDragMarker();
    };

    const onMouseMove = (event: MapMouseEvent) => {
      updateFreeDraw(event);
      updateDragMarker(event);
    };

    registerEventHandler(osmLinesId, 'click', onOsmLineClick);
    registerEventHandler(osmLinesId, 'mousedown', onMouseDown);
    // Since chevrons render above the polyline, treat a click on a chevron as a click on the polyline
    registerEventHandler(customRouteActiveSegmentChevronsId, 'click', onActivePolylineClick);
    registerEventHandler(customRouteActiveSegmentPolylineId, 'click', onActivePolylineClick);
    registerEventHandler(customRouteMarkersId, 'mousedown', onMarkerMouseDown);
    registerEventHandler(customRouteMarkersId, 'mouseenter', onMarkerMouseEnter);
    registerEventHandler(customRouteMarkersId, 'mouseleave', onMarkerMouseLeave);
    registerEventHandler(customRouteCutSegmentEndMarkerId, 'click', onCutSegmentEndMarkerClick);
    registerBaseMapEventHandlers({
      click: { handler: onMapClick },
      mousedown: { handler: onMouseDown },
      // We want to handle drawing that started on the base map but ended on a layer
      mouseup: { handler: onMouseUp, allLayers: true },
      mousemove: { handler: onMouseMove, allLayers: true }
    });

    return () => {
      unregisterEventHandler(osmLinesId, 'click', onOsmLineClick);
      unregisterEventHandler(osmLinesId, 'mousedown', onMouseDown);
      unregisterEventHandler(customRouteActiveSegmentChevronsId, 'click', onActivePolylineClick);
      unregisterEventHandler(customRouteActiveSegmentPolylineId, 'click', onActivePolylineClick);
      unregisterEventHandler(customRouteMarkersId, 'mousedown', onMarkerMouseDown);
      unregisterEventHandler(customRouteMarkersId, 'mouseenter', onMarkerMouseEnter);
      unregisterEventHandler(customRouteMarkersId, 'mouseleave', onMarkerMouseLeave);
      unregisterEventHandler(customRouteCutSegmentEndMarkerId, 'click', onCutSegmentEndMarkerClick);
      unregisterBaseMapEventHandlers(['click', 'mousedown', 'mouseup', 'mousemove']);
    };
  }, [
    isDrawingEnabled,
    isInMarkerRef,
    isContextMenuOpenRef,
    getIsActionPending,
    getIsActionStarting,
    overrideSmartRouting,
    restoreSmartRouting,
    resetCursor,
    routeToPoint,
    addHandle,
    startFreeDraw,
    updateFreeDraw,
    saveFreeDraw,
    startDragMarker,
    updateDragMarker,
    saveDragMarker,
    dispatch,
    registerEventHandler,
    unregisterEventHandler,
    registerBaseMapEventHandlers,
    unregisterBaseMapEventHandlers,
    isPOISelected,
    isMapFeatureSelected,
    cutData
  ]);

  const contextValue = useMemo(
    () => ({
      pendingHandle,
      dragMarker,
      hoveredMarker,
      freeDrawPoints,
      isContextMenuOpen,
      isContextMenuOpenRef,
      setIsContextMenuOpen,
      showPositionTooltip: options?.showPositionTooltip ?? false
    }),
    [pendingHandle, dragMarker, hoveredMarker, freeDrawPoints, isContextMenuOpen, isContextMenuOpenRef, setIsContextMenuOpen]
  );

  return (
    <DrawControlsContext.Provider value={contextValue}>
      {/* Before the children so its layer is added before the route's: the route's layers order above it as they are
          added, while its own position is read during a render, before layers in the same render are on the map */}
      {options?.linkNearbySegmentEndpoints && isDrawingEnabled && <LinkableEndpointIndicator dragMarker={dragMarker} />}
      {children}
    </DrawControlsContext.Provider>
  );
};

export default DrawControlsProvider;
